// ── participant-dashboard-helpers: module access + own-data read glue (Wave 9) ─
// The Participant Dashboard is the participant's COMPLETE self-service home and
// the ONLY surface a participant ever sees. Its two release gates are
// FR-PDASH-001 (participant isolation — own data only) and FR-REPORT-001 (report
// exclusion — zero reports anywhere). This module carries the access guard and
// the shared own-data read helpers; the sections consume them.
//
// FAIL-CLOSED ALLOWLIST: only `participant` may load the Participant Dashboard
// home. Staff/coach/company_admin/cohort_lead land on their own role dashboards;
// they never render this learner-private projection. (The dashboard route is
// allRoles so the record-scope chain resolves, but the BODY chooses this
// projection ONLY for participant — see product-participant-dashboard-surface.)

import type { CoachingRole } from "../../domain/roles";
import {
  getCoachingServices,
  getCoachingZ6Services,
  getCoachingReadinessServices,
} from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type {
  CoachingCohortDto,
  CoachingProfileDto,
  CoachingSessionDto,
  ListResponse,
} from "../../services/coaching-dtos";
import type {
  CoachingAssignmentLogDto,
  CoachingSessionRecordingsDto,
} from "../../services/coaching-z6-dtos";
import type {
  CoachingCapstoneSubmissionsResponseDto,
  CoachingCertificatesResponseDto,
} from "../../services/coaching-readiness-dtos";

/** The role for whom this dashboard is the home projection. */
export function isParticipantDashboardRole(role: CoachingRole): boolean {
  return role === "participant";
}

// ── Own-data read fns (server-scoped; the projection wraps each in useHonestQuery) ─
// Each fn is a thin, report-FREE call into the EXISTING typed services. None of
// them names a report endpoint (FR-REPORT-001) and none requests a cross-
// participant list (FR-PDASH-001). The server pins each to the participant's own
// data; the projection re-filters sessions to the own-cohort set as defense-in-
// depth (see domain/participant-scope.ownSessionsOnly).

/** OWN identity / profile projection (read-only). */
export function readOwnProfile(): Promise<CoachingProfileDto> {
  return getCoachingServices().auth.me();
}

/** OWN cohorts only — server-scoped (scope=mine). NEVER the admin cohort list. */
export function readOwnCohorts(): Promise<ListResponse<CoachingCohortDto>> {
  return getCoachingServices().cohorts.list({ scope: "mine" });
}

/** OWN cohort's sessions — server-scoped to the participant's enrolment. */
export function readOwnCohortSessions(cohortId: string): Promise<ListResponse<CoachingSessionDto>> {
  return getCoachingServices().sessions.listForCohort(cohortId);
}

/** OWN session recordings — presence-only passcode; dark lane → honest empty. */
export function readOwnSessionRecordings(sessionId: string): Promise<CoachingSessionRecordingsDto> {
  return getCoachingZ6Services().recordings.forSession(sessionId);
}

/** OWN cohort assignment context — Jotform-sourced; dark lane → honest empty. */
export function readOwnCohortAssignments(cohortId: string): Promise<CoachingAssignmentLogDto> {
  return getCoachingZ6Services().assignments.forCohort(cohortId);
}

/**
 * OWN capstone submission(s) — the server scope-isolates this read to the
 * participant's OWN submission(s) (participant ownership predicate in the repo).
 * We read by the participant's OWN cohort id and NEVER request a cross-cohort or
 * cross-participant roster. reportId is a REFERENCE only — no report body here.
 */
export function readOwnCapstoneSubmissions(cohortId: string): Promise<CoachingCapstoneSubmissionsResponseDto> {
  return getCoachingReadinessServices().capstone.listForCohort(cohortId);
}

/**
 * OWN certificate(s) — the server scope-isolates this read to the participant's
 * OWN certificate(s). artifactRef may be null (document pending) — never a
 * fabricated download link.
 */
export function readOwnCertificates(): Promise<CoachingCertificatesResponseDto> {
  return getCoachingReadinessServices().certificates.list();
}

// ── Query keys (re-use the existing key factory so reads dedupe with My Cohorts) ─
export const participantQueryKeys = {
  profile: () => coachingQueryKeys.profile(),
  ownCohorts: () => coachingQueryKeys.list("cohorts.list", { scope: "mine" }),
  cohortSessions: (cohortId: string) =>
    coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }),
  cohortSessionsForCohorts: (cohortIds: string[]) =>
    coachingQueryKeys.list("sessions.listForCohort", { cohort_ids: cohortIds.join(",") }),
  recordings: (sessionId: string) => coachingQueryKeys.z6.recordings(sessionId),
  assignments: (cohortId: string) => coachingQueryKeys.z6.assignments(cohortId),
  capstone: (cohortId: string) => coachingQueryKeys.readiness.capstoneForCohort(cohortId),
  certificates: () => coachingQueryKeys.readiness.certificates(),
};

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

/** The participant's own display name, falling back to a neutral label. */
export function ownDisplayName(profile: CoachingProfileDto | undefined): string {
  const name = (profile?.name ?? "").trim();
  return name.length > 0 ? name : "there";
}
